import { useState, type FormEvent } from "react";
import { useMutation } from "convex/react";
import { Link, useNavigate } from "react-router-dom";

import { api } from "../../../../convex/_generated/api";
import { ArrowLeftIcon, CheckIcon } from "../components/icons";
import { PageHeader } from "../components/shell";
import { Button, Field, Notice } from "../components/ui";
import { describeOperatorError } from "../lib/errors";
import type { NewOrganisationDetails } from "./types";

function normalizeDetails(details: NewOrganisationDetails): NewOrganisationDetails {
  return {
    name: details.name.trim().replace(/\s+/g, " "),
    administratorName: details.administratorName.trim().replace(/\s+/g, " "),
    administratorEmail: details.administratorEmail.trim().toLowerCase(),
  };
}

export function CreateOrganisationView({
  onCreate,
}: {
  onCreate: (details: NewOrganisationDetails) => Promise<void>;
}) {
  const [details, setDetails] = useState<NewOrganisationDetails>({
    name: "",
    administratorName: "",
    administratorEmail: "",
  });
  const [reviewing, setReviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const normalized = normalizeDetails(details);

  function review(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (!normalized.name || !normalized.administratorName) {
      setError("Enter the organisation and administrator names.");
      return;
    }
    if (!normalized.administratorEmail.includes("@")) {
      setError("Enter a valid administrator email address.");
      return;
    }
    setDetails(normalized);
    setReviewing(true);
  }

  async function create() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(normalized);
    } catch (caught) {
      setError(describeOperatorError(caught, "create"));
      setBusy(false);
    }
  }

  return (
    <section>
      <Link className="back-link" to="/">
        <ArrowLeftIcon /> Back to organisations
      </Link>
      <PageHeader eyebrow="Provisioning" title={reviewing ? "Confirm new organisation" : "Create an organisation"}>
        {reviewing
          ? "Review the workspace and first administrator before creating both records atomically."
          : "Create the workspace and nominate its first administrator in one operation."}
      </PageHeader>

      <div className="form-panel">
        <div className="form-progress" aria-label="Create organisation progress">
          <span className={reviewing ? "complete" : "active"}>Details</span>
          <i aria-hidden="true" />
          <span className={reviewing ? "active" : ""}>Confirm</span>
        </div>
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {!reviewing ? (
          <form className="form-stack" onSubmit={review} noValidate>
            <Field label="Organisation name" hint="Shown as the customer workspace name.">
              <input
                autoFocus
                autoComplete="organization"
                maxLength={120}
                onChange={(event) => setDetails({ ...details, name: event.target.value })}
                placeholder="Southern Survey Group"
                value={details.name}
              />
            </Field>
            <div className="field-grid">
              <Field label="First administrator name">
                <input
                  autoComplete="name"
                  maxLength={120}
                  onChange={(event) => setDetails({ ...details, administratorName: event.target.value })}
                  placeholder="Alex Morgan"
                  value={details.administratorName}
                />
              </Field>
              <Field label="First administrator email" hint="This can be your own operator email.">
                <input
                  autoComplete="email"
                  inputMode="email"
                  onChange={(event) => setDetails({ ...details, administratorEmail: event.target.value })}
                  placeholder="alex@example.com"
                  type="email"
                  value={details.administratorEmail}
                />
              </Field>
            </div>
            <div className="form-actions">
              <Button type="submit">Review organisation</Button>
              <Link className="button button--quiet" to="/">Cancel</Link>
            </div>
          </form>
        ) : (
          <div className="confirmation-sheet">
            <dl>
              <div><dt>Organisation</dt><dd>{normalized.name}</dd></div>
              <div><dt>First administrator</dt><dd>{normalized.administratorName}</dd></div>
              <div><dt>Invitation target</dt><dd>{normalized.administratorEmail}</dd></div>
            </dl>
            <Notice tone="warning" title="One atomic operation">
              This creates the organisation and invited administrator, then schedules the existing onboarding email. The invitation target cannot be corrected here later.
            </Notice>
            <div className="form-actions">
              <Button busy={busy} onClick={() => void create()}>
                <CheckIcon /> Create organisation
              </Button>
              <Button tone="quiet" disabled={busy} onClick={() => setReviewing(false)}>
                Edit details
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function CreateOrganisationRoute() {
  const provision = useMutation(api.organisations.provision);
  const navigate = useNavigate();

  return (
    <CreateOrganisationView
      onCreate={async (details) => {
        await provision(details);
        navigate("/", {
          replace: true,
          state: { success: `Created ${details.name}. The first-administrator invitation was scheduled.` },
        });
      }}
    />
  );
}
